import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { useSelector } from 'react-redux';
import searchIcon from '../../assets/SidebarIcons/icon-search.svg';
import config from '../../config/Constant';
import {
  downloadFile,
  getRequest,
  postRequest,
} from '../../helpers/apiHandler';
import CustomAlert from '../CustomAlert';
import BlacklistModal from './BlacklistModal';
import moment from 'moment/moment';

const white_label_perkeso = process.env.REACT_APP_WHITE_LABEL_PERKESO === 'true';

const Blacklist = () => {
  const { access_token } = useSelector((state) => state.auth.user);
  const [loadingData, setLoadingData] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [search, setSearch] = useState('');
  const [data, setData] = useState([]);
  const [searchData, setSearchData] = useState([]);
  const [columns, setColumns] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [selectedPhone, setSelectedPhone] = useState('+60');
  const [selectedId, setSelectedId] = useState('');
  const [loadingDownload, setLoadingDownload] = useState(false);

  const [alertState, setAlertState] = useState({
    show: false,
    variant: 'danger',
    message: '',
  });

  const fetch_data = useCallback(() => {
    setLoadingData(true);
    const requestData = { headers: { Authorization: access_token.token } };
    getRequest(`/admin/blacklist`, requestData, (res) => {
      if (res.success) {
        const result = res.data;
        setData(result);
        setSearchData(result);
        generateColumns(result);
      }
      setLoadingData(false);
    });
  }, [access_token.token]);

  const generateColumns = (data) => {
    if (data.length > 0) {
      const generatedColumns = [
        {
          name: "ID",
          sortable: true,
          selector: (row) => row.id,
        },
        {
          name: "Phone Number",
          sortable: true,
          selector: (row) => row.phone_number,
        },
        {
          name: "Created At",
          sortable: true,
          selector: (row) => row.created_at,
        },
      ]
      setColumns(generatedColumns);

      generatedColumns.push({
        name: 'Actions',
        minWidth: '250px',
        selector: (row) => (
          <div className="d-flex justify-content-center align-items-center gap-1">
            <Button variant="primary" style={{ minWidth: 100 }} onClick={() => handleEdit(row)}>
              Edit
            </Button>
            <Button variant="ghost" style={{ minWidth: 100 }} onClick={() => handleDelete(row)}>
              Delete
            </Button>
          </div>
        ),
      });
    }
  };

  const handleDownload = () => {
    setLoadingDownload(true);
    downloadFile(
      {
        url: '/admin/blacklist-download',
        access_token: access_token,
        fileName: 'blacklist-data.csv',
      },
      (res) => {
        setLoadingDownload(false);
        if (res.success) {
          setAlertState({
            message: 'Your blacklist data have been successfully exported to csv.',
            show: true,
            variant: 'success',
          });
        } else {
          setAlertState({
            message:
              'We apologize, but it seems there was an issue exporting your blacklist data to csv.',
            show: true,
            variant: 'danger',
          });
        }
      },
    );
  };

  const handleEdit = (row) => {
    setSelectedPhone(row.phone_number);
    setSelectedId(row.id);
    setModalType('edit');
    setShowModal(true);
  };

  const handleDelete = (row) => {
    setSelectedPhone(row.phone_number);
    setSelectedId(row.id);
    setModalType('delete');
    setShowModal(true);
  };

  const handleCreate = () => {
    setSelectedPhone('+60');
    setModalType('create');
    setShowModal(true);
  };

  const handleModalSubmit = (phoneNumber) => {
    setLoadingModal(true);
    let requestData = {};

    if (modalType === 'create') {
      requestData = { phone: phoneNumber };
    } else {
      requestData = { phone: phoneNumber, id: selectedId };
    }

    let apiEndpoint = '';
    let message = '';
    if (modalType === 'create') {
      apiEndpoint = '/admin/blacklist';
      message = "Successfully blacklist number."
    } else if (modalType === 'edit') {
      apiEndpoint = '/admin/blacklist-update';
      message = "Successfully update blacklist number."
    } else if (modalType === 'delete') {
      apiEndpoint = '/admin/blacklist-delete';
      message = "Successfully remove blacklist number."
    }

    postRequest(apiEndpoint, requestData, access_token.token, (res) => {
      if (res.success) {
        setAlertState({
          show: true,
          variant: 'success',
          message: message,
        });
        fetch_data();
      } else {
        setAlertState({
          show: true,
          variant: 'danger',
          message: res.message,
        });
      }
      setLoadingModal(false);
      setShowModal(false);
      setSelectedId('');
      setSelectedPhone('+60');
      fetch_data();
    });
  };

  const onSearch = (evt) => {
    const { value } = evt.target;
    const filteredData = data.filter(
      (item) =>
        item.phone_number && item.phone_number.includes(value)
    );

    setSearch(value);
    setSearchData(value ? filteredData : data);
  };

  useEffect(() => {
    fetch_data();
  }, []);

  useEffect(() => {
    if (alertState.show) {
      setTimeout(() => {
        setAlertState({
          message: '',
          show: false,
          variant: '',
        });
      }, config.notify_time);
    }
  }, [alertState]);

  return (
    <div className="w-100">
      <div className="page-heading admin-heading">
        <div className="container-left" style={{ marginBottom: "32px"}}>
          <div className="page-title">Blacklisted Numbers</div>
        </div>
        <div className="w-100 d-flex justify-content-between">
          <div className="w-50">
            <InputGroup className="datatable-search search-input">
              <InputGroup.Append>
                <InputGroup.Text>
                  <img alt="..." src={searchIcon} />
                </InputGroup.Text>
              </InputGroup.Append>
              <Form.Control
                placeholder="Search..."
                name="search"
                value={search}
                onChange={onSearch}
                className="search-form"
              />
            </InputGroup>
          </div>
          <div className='d-flex gap-1'>
            <Button variant="primary" style={{ minWidth: 100 }} onClick={handleCreate}>
              Create
            </Button>
            { white_label_perkeso && <Button
              variant="primary"
              onClick={handleDownload}
              disabled={loadingDownload}
            >
              {loadingDownload && <Spinner animation="border" size="sm" className="mr-2" />}
              Download
            </Button>}
          </div>
        </div>
      </div>
      <CustomAlert
        message={alertState.message}
        show={alertState.show}
        variant={alertState.variant}
        closeAlert={() => setAlertState({ show: false, variant: 'danger', message: '' })}
      />
      <DataTable
        columns={columns}
        data={searchData}
        paginationComponentOptions={{
          rowsPerPageText: 'Items per page:',
        }}
        pagination
      />
      <BlacklistModal
        type={modalType}
        show={showModal}
        onHide={() => setShowModal(false)}
        onSubmit={handleModalSubmit}
        loading={loadingModal}
        phoneNumber={selectedPhone}
      />
    </div>
  );
};

export default Blacklist;
